import React from 'react';
import { Box, Typography } from '@mui/material';
import {
  ArrowForward, ArrowForwardIos, Assignment, Description, Factory, Inventory2,
  LocalShipping, Payments, PersonSearch, ReceiptLong, VerifiedUser,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { FLOW } from './missionData';
import { s, cardSx, trendTone, focusRing, reduceMotion } from './missionTokens';

const ICONS: Record<string, React.ElementType> = {
  leads: PersonSearch,
  so: Description,
  proc: Assignment,
  prod: Factory,
  qc: VerifiedUser,
  inv: Inventory2,
  dispatch: LocalShipping,
  invoice: ReceiptLong,
  payment: Payments,
};

const TINT: Record<string, string> = {
  leads: s.primary, so: s.success, proc: s.warning, prod: s.info, qc: s.violet,
  inv: s.success, dispatch: s.primary, invoice: s.warning, payment: s.violet,
};

/**
 * Business Flow — opportunity to cash, as a navigable spine.
 *
 * Each stage carries its own volume and trend and routes straight into that
 * operational workspace, so the strip is a way *into* the business rather than
 * a diagram of it. It wraps rather than scrolling: a stage hidden off-screen
 * behind a scrollbar is a stage nobody acts on.
 */
const BusinessFlowStrip: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Box sx={{ ...cardSx, p: 2.25 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: '0.98rem', fontWeight: 800, color: s.heading }}>
          Business Flow
        </Typography>
        <Typography sx={{ fontSize: '0.78rem', color: s.muted }}>
          From opportunity to cash. Click any stage to dive in.
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Box
          component="button"
          onClick={() => navigate('/reports')}
          sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.5, border: 0, background: 'none',
            cursor: 'pointer', fontSize: '0.78rem', fontWeight: 700, color: s.primary, borderRadius: 1,
            '& .arw': { transition: 'transform .25s ease' },
            '&:hover .arw': { transform: 'translateX(4px)' },
            ...focusRing, ...reduceMotion,
          }}
        >
          End-to-End View
          <ArrowForward className="arw" sx={{ fontSize: 15 }} />
        </Box>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(3, minmax(0,1fr))',
            sm: 'repeat(5, minmax(0,1fr))',
            lg: 'repeat(9, minmax(0,1fr))',
          },
          gap: 0.5,
        }}
      >
        {FLOW.map((stage, i) => {
          const Icon = ICONS[stage.key];
          const tint = TINT[stage.key];
          const { up, color } = trendTone(stage.delta);

          return (
            <Box key={stage.key} sx={{ position: 'relative', display: 'flex', alignItems: 'flex-start' }}>
              <Box
                component="button"
                onClick={() => navigate(stage.href)}
                aria-label={`${stage.label}: ${stage.count}, ${up ? 'up' : 'down'} ${Math.abs(stage.delta)} percent`}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  border: '1px solid transparent',
                  background: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 0.6,
                  px: 0.5,
                  py: 1,
                  borderRadius: 2,
                  transition: 'background-color .22s ease, border-color .22s ease, transform .22s ease',
                  '&:hover': {
                    backgroundColor: s.canvas,
                    borderColor: s.border,
                    transform: 'translateY(-2px)',
                  },
                  '&:hover .flow-ico': { transform: 'scale(1.1)' },
                  ...focusRing,
                  ...reduceMotion,
                }}
              >
                <Box
                  className="flow-ico"
                  sx={{
                    width: 34, height: 34, borderRadius: 2, display: 'grid', placeItems: 'center',
                    backgroundColor: `${tint}16`,
                    transition: 'transform .22s ease',
                    ...reduceMotion,
                  }}
                >
                  <Icon sx={{ fontSize: 17, color: tint }} />
                </Box>
                <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: s.body, textAlign: 'center', lineHeight: 1.25 }}>
                  {stage.label}
                </Typography>
                <Typography sx={{ fontSize: '0.95rem', fontWeight: 800, color: s.heading, lineHeight: 1.1 }}>
                  {stage.count}
                </Typography>
                <Typography sx={{ fontSize: '0.66rem', fontWeight: 700, color }}>
                  {up ? '↑' : '↓'} {Math.abs(stage.delta)}%
                </Typography>
              </Box>

              {/* Connector — hidden on the last stage of each row so a wrapped
                  grid never points at nothing. */}
              {i < FLOW.length - 1 && (
                <ArrowForwardIos
                  className="flow-sep"
                  sx={{
                    position: 'absolute',
                    right: -5,
                    top: 24,
                    fontSize: 9,
                    color: s.muted,
                    opacity: 0.55,
                    display: { xs: 'none', lg: 'block' },
                  }}
                />
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default BusinessFlowStrip;
